import { Link, useNavigate } from 'react-router-dom';
import { Box, Grid2, List, ListItem, ListItemIcon, ListItemText, Stack, Typography, useMediaQuery } from '@mui/material';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import Carousel from 'react-material-ui-carousel';
import styled from '@emotion/styled';
import { Container, TitleBar, SmartphoneButton, UilCalenderButton, UilCalenderIcon, SmartphoneWifiIcon, TopNextButton } from 'components';
import { baseColors } from 'utils/constants';
import { DotsText, SpeechBubble } from './style';
import 'top.css';

// asset
import sliderImage1 from 'assets/images/top/pc-fv.png';
import sliderSpImage1 from 'assets/images/top/sp-fv01.png';
import sliderSpImage2 from 'assets/images/top/sp-fv02.png';
import mainBodyImage from 'assets/images/top/main-body.png';
import air1Image from 'assets/images/top/air-1.png';
import mobileRouterImage from 'assets/images/top/mobile-router.png';
import siteLogo from 'assets/images/wifi-tokyo-logo.png';
import cityImage from 'assets/images/top/city-silhouette.png';
import homeRouterImage from 'assets/images/top/home-router.png';
import wimaxBodyImage from 'assets/images/top/wimax-body.png';
import air1BodyImage from 'assets/images/top/air1-body.png';
import flow01Image from 'assets/images/top/top-flow1.png';
import flow02Image from 'assets/images/top/top-flow2.png';
import flow03Image from 'assets/images/top/top-flow3.png';
import visaImage from 'assets/images/brand/visa.png';
import mastercardImage from 'assets/images/brand/mastercard.png';
import jcbImage from 'assets/images/brand/jcb.png';
import amexImage from 'assets/images/brand/amex.png';
import dinersImage from 'assets/images/brand/diners.png';
import amazonImage from 'assets/images/brand/amazon.png';
import wifiTokyoImage from 'assets/images/top/footer-wifi-tokyo.png';

// types
import PlanCard from 'sections/top/PlanCard';
import OtherPlanCard from 'sections/top/OtherPlanCard';
import ExtendCard from 'sections/top/ExtendCard';
import RecommendCard from 'sections/top/RecommendCard';
import UsageFlowCard from 'sections/top/UsageFlowCard';
import TopDetailButton from 'components/TopDetailButton';
import BillIcon from 'components/icons/Bill';
import InternetIcon from 'components/icons/Internet';
import MapIcon from 'components/icons/Map';
import TelIcon from 'components/icons/Tel';
import MailIcon from 'components/icons/Mail';
import ClockIcon from 'components/icons/Clock';
import FlowArrowRIcon from 'components/icons/FlowArrowR';
import BannerSlider from 'components/BannerSlider';
import TopFooterMenu from 'components/TopFooterMenu';
import FlowArrowDIcon from 'components/icons/FLowArrowD';
import FaqAccordion from 'sections/top/FaqAccordion';
import VerticalButton from 'components/VerticalButton';
import CompensationBlock from 'sections/top/CompensationBlock';

//const isMobile = window.innerWidth < 768;
// テーマを作成
const theme = createTheme({
  components: {
    MuiListItem: {
      styleOverrides: {
        root: {
          alignItems: 'flex-start'
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          margin: '4px 0px'
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '14px',
          paddingLeft: '5px'
        }
      }
    }
  }
});

const sliderImages = [
  { src: sliderImage1, mobileSrc: sliderSpImage1 },
  { src: sliderImage1, mobileSrc: sliderSpImage2 }
];

const FlowSpan = styled('span')(({ theme }) => ({
  background: `linear-gradient(transparent 60%, ${baseColors.yellow} 30%)`,
  display: 'inline-block'
}));

const Top = () => {
  const uTheme = useTheme();
  const isMobile = useMediaQuery(uTheme.breakpoints.down('sm')); // サイズを確認
  const recommendLists1 = ['初期費用なし', '解約手数料なし', '本人確認書類必要なし'];
  const recommendTexts2 = ['同梱の返却用袋で返却するだけ', 'ポストに投函するだけ'];
  const recommendLists3 = ['即日発送（16時までにお申し込み）', '郵送でも最短翌日受け取り', '店舗受け取りなら当日レンタル可能！'];
  const recommendAttention2 = ['※ホームルーターは除く'];
  const planLists1 = ['サクッとプラン', 'たっぷりプラン', 'ピッタリプラン', '無制限プランモバイルルーター'];
  const planLists2 = ['無制限プランホームルーター'];

  const navigate = useNavigate();
  const handleNext = (url?: string): void => {
    if (url) {
      if (url.startsWith('http')) {
        window.location.href = url;
      } else {
        navigate(url);
      }
    } else {
      navigate('/rental/ec');
    }
  };

  return (
    <>
      <Container
        id="item-select"
        sx={{
          pt: 0,
          width: { xs: '100%', sm: '1000px' },
          minWidth: { xs: '375px', sm: '1000px' }
        }}
      >
        <Box sx={{ width: '100%', margin: '20px auto', maxWidth: 1000 }}>
          <Carousel>
            {sliderImages.map((image, index) => (
              <div key={index}>
                <img src={isMobile ? image.mobileSrc : image.src} alt={`slide-${index}`} style={{ width: '100%' }} />
              </div>
            ))}
          </Carousel>
        </Box>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px'
          }}
        >
          <SmartphoneButton />
          <UilCalenderButton />
        </Stack>
        <TitleBar>プラン紹介</TitleBar>
        <Box component="section" sx={{ p: '20px 0' }}>
          <Box sx={{ padding: '20px', background: baseColors.gray }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <PlanCard
                mainBodyImage={mainBodyImage}
                air1Image={air1Image}
                price={330}
                title="サクッとプラン"
                subText="1日に必要最低限の"
                subText2="3GB"
                rate={1}
                plan="1"
              />
              <PlanCard
                mainBodyImage={mainBodyImage}
                air1Image={air1Image}
                price={440}
                title="たっぷりプラン"
                subText="1日に十分な"
                subText2="5GB"
                rate={2}
                plan="2"
              />
              <PlanCard
                mainBodyImage={mainBodyImage}
                air1Image={air1Image}
                price={385}
                title="ピッタリプラン"
                subText="自分でコントロールで"
                subText2="自由に"
                rate={3}
                url="https://www.wifi-tokyo-rentalshop.com/pittariplan/?itm_source=lp&itm_medium=organic&itm_campaign=lp_saide_L"
              />
            </Stack>
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                fontSize: '12px',
                color: baseColors.blackGray,
                padding: '20px 0'
              }}
            >
              ※サクッとプランとたっぷりプランに関しましては1日3GB/5GBを超過した場合でも128Kbpsの低速通信でご利用いただけます。
            </Typography>
          </Box>
          <Box
            sx={{
              marginTop: '20px',
              padding: '20px',
              textAlign: 'center',
              background: baseColors.gray
            }}
          >
            <SpeechBubble>
              容量を気にしたくない<DotsText>あなた</DotsText>は
            </SpeechBubble>
            <Typography variant="subtitle1" sx={{ color: baseColors.red, fontSize: '28px' }}>
              無制限プラン
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <OtherPlanCard
                mainBodyImage={mobileRouterImage}
                price={660}
                title="モバイルルーター"
                subText="場所を選ばず"
                subText2="どこでも便利！"
                plan="4"
              />
              <OtherPlanCard
                mainBodyImage={homeRouterImage}
                price={660}
                title="ホームルーター"
                subText="工事不要で"
                subText2="コンセントに挿すだけ！"
                plan="5"
              />
            </Stack>
          </Box>
          <Box
            sx={{
              marginTop: '20px',
              padding: '20px',
              textAlign: 'center',
              background: baseColors.gray
            }}
          >
            <SpeechBubble style={{ fontSize: '22px' }}>すでにご利用の方</SpeechBubble>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{
                paddingTop: '10px',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center'
              }}
            >
              <ExtendCard mainBodyImage={air1BodyImage} mainBodyImage2={wimaxBodyImage} title="レンタル延長" />
            </Stack>
          </Box>
          <Box sx={{ marginTop: '20px', padding: '20px', textAlign: 'center' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'end',
                fontSize: '24px',
                color: baseColors.black,
                fontWeight: 'bold'
              }}
            >
              <img src={siteLogo} alt="logo" style={{ width: '169px' }} />
              <span>の</span>
            </div>
            <Typography sx={{ fontSize: '28px', fontWeight: 'bold' }}>おすすめポイント</Typography>
            <img src={cityImage} alt="city" style={{ width: '232px' }} />
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <RecommendCard width={310} number="01" title="面倒な手続きなし！" recommendLists={recommendLists1} />
            <RecommendCard
              width={310}
              number="02"
              title="返却もカンタン！"
              recommendTexts={recommendTexts2}
              recommendAttentions={recommendAttention2}
            />
            <RecommendCard width={330} number="03" title="最短で当日使える！" recommendLists={recommendLists3} />
          </Stack>
        </Box>
        <TitleBar>ご利用の流れ</TitleBar>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          spacing={0}
          sx={{
            margin: '25px auto',
            padding: '45px 0 15px 0',
            justifyContent: 'center',
            backgroundColor: baseColors.lightGray
          }}
        >
          <UsageFlowCard
            number="01"
            flowImage={flow01Image}
            title="ネットで簡単予約"
            explanatoryText={
              <span>
                ショッピングサイトでお買い物をするのと同じ手続きで簡単、
                <FlowSpan style={{ color: baseColors.black }}>
                  <strong>最短3分</strong>でレンタル可能
                </FlowSpan>
                です！
              </span>
            }
          />
          <div style={{ padding: '10px' }}>{isMobile ? <FlowArrowDIcon /> : <FlowArrowRIcon />}</div>
          <UsageFlowCard
            number="02"
            flowImage={flow02Image}
            title="指定日・指定場所で受取"
            explanatoryText={
              <span>
                <FlowSpan style={{ color: baseColors.black }}>
                  <strong>16時まで</strong>にご予約いただければ当日発送可能
                </FlowSpan>
                です。店舗受け取りなら当日から利用できます。
              </span>
            }
          />
          <div style={{ padding: '10px' }}>{isMobile ? <FlowArrowDIcon /> : <FlowArrowRIcon />}</div>
          <UsageFlowCard
            number="03"
            flowImage={flow03Image}
            title="使い終わったら簡単返却"
            explanatoryText={
              <span>
                レンタル品をすべて返却用袋に同封したら、あとは
                <FlowSpan style={{ color: baseColors.black }}>
                  最寄りの<strong>ポストに投函</strong>
                </FlowSpan>
                するだけです！
              </span>
            }
          />
        </Stack>
        <TitleBar>送料について</TitleBar>
        <Typography variant="subtitle1" sx={{ padding: '15px 0', textAlign: 'center' }}>
          全国一律料金
        </Typography>
        <Grid2 container spacing={3} sx={{ width: { xs: '100%', sm: '800px' }, margin: '0 auto' }}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <List
              sx={{
                padding: '10px',
                height: '126px',
                backgroundColor: baseColors.lightGray
              }}
            >
              {planLists1.map((text, index) => (
                <ListItem key={index} sx={{ display: 'flex', alignItems: 'center', padding: 0 }}>
                  <ListItemIcon sx={{ minWidth: '24px' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      ・
                    </Typography>
                  </ListItemIcon>
                  <ListItemText primary={text} sx={{ margin: '0' }} primaryTypographyProps={{ padding: '0px' }} />
                </ListItem>
              ))}
            </List>
            <Typography variant="subtitle1" sx={{ padding: '15px 0', textAlign: 'center' }}>
              【往復送料】1,100円 (税込)
            </Typography>
            <Typography sx={{ fontSize: '14px' }}>
              ※返送用ゆうパケット550円含む。
              <br />
              ※複数台契約の場合は送料とご返送手続きが異なりますが、ご返却期限はレンタル終了日翌日午前中(12時)までとなります。
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <List
              sx={{
                padding: '10px',
                height: '126px',
                backgroundColor: baseColors.lightGray
              }}
            >
              {planLists2.map((text, index) => (
                <ListItem key={index} sx={{ display: 'flex', alignItems: 'center', padding: 0 }}>
                  <ListItemIcon sx={{ minWidth: '24px' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      ・
                    </Typography>
                  </ListItemIcon>
                  <ListItemText primary={text} sx={{ margin: '0' }} primaryTypographyProps={{ padding: '0px' }} />
                </ListItem>
              ))}
            </List>
            <Typography variant="subtitle1" sx={{ padding: '15px 0', textAlign: 'center' }}>
              【発送料】550円 (税込)
            </Typography>
            <Typography sx={{ fontSize: '14px', paddingBottom: '10px' }}>
              ※返送料：お客様ご負担
              <br />
              ※レンタル終了日翌日午前中(12時)までに「追跡番号が分かるもの」でご返送手続きをお願いいたします。
              <br />
              ※お手数ですが、ご返送手続きいただいた際に当社まで「配送業者と追跡番号(送り状番号)」をメールでご連絡ください。
            </Typography>
          </Grid2>
        </Grid2>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{
            backgroundColor: baseColors.lightPink,
            justifyContent: 'space-between',
            alignItems: 'center',
            width: { xs: '100%', sm: '800px' },
            padding: '15px',
            margin: '0 auto'
          }}
        >
          <Typography component="div" variant="body1" sx={{ padding: '0' }}>
            <List sx={{ padding: '0' }}>
              <ListItem sx={{ padding: '0', alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: '24px' }}>
                  <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                    ・
                  </Typography>
                </ListItemIcon>
                <ListItemText sx={{ margin: '0' }}>
                  <Typography
                    sx={{
                      padding: '0px',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    平日の16時までのお申込み分は、当日に発送いたします。
                  </Typography>
                </ListItemText>
              </ListItem>
              <ListItem sx={{ padding: '0', alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: '24px' }}>
                  <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                    ・
                  </Typography>
                </ListItemIcon>
                <ListItemText sx={{ margin: '0', padding: '0' }}>
                  <Typography
                    sx={{
                      padding: '0px',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    郵送の場合、最短で翌日からのご利用が可能です。
                  </Typography>
                </ListItemText>
              </ListItem>
              <ListItem sx={{ padding: '0', alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: '24px' }}>
                  <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                    ・
                  </Typography>
                </ListItemIcon>
                <ListItemText sx={{ margin: '0' }}>
                  <Typography
                    sx={{
                      padding: '0px',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    土日祝日にお申込みの場合は翌営業日に発送となります。
                  </Typography>
                </ListItemText>
              </ListItem>
            </List>
            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
              <span style={{ color: baseColors.red }}>※土日祝日</span>
              は発送を行なっていません。
              <br />
              <span style={{ color: baseColors.red }}>※お届け指定日</span>
              が利用開始日になります。
            </Typography>
          </Typography>
          <div style={{ padding: '15px' }}>
            <TopDetailButton width="300px">
              空港・ホテル受け取りの
              <br />
              お客様はご確認ください
            </TopDetailButton>
          </div>
        </Stack>
        <TitleBar sx={{ marginTop: '20px' }}>店頭受取について</TitleBar>
        <Box
          sx={{
            textAlign: 'center',
            width: '100%',
            margin: '0 auto',
            paddingBottom: '20px'
          }}
        >
          <Box
            component="div"
            sx={{
              position: 'relative',
              width: '100%',
              height: '0',
              paddingTop: '50%'
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
            <iframe
              title="Google Maps Location"
              style={{
                border: 0,
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%'
              }}
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3240.25095432044!2d139.69823!3d35.6954416!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188cd628197a3f%3A0x66d37049da8deb4e!2z44CSMTYwLTAwMjMg5p2x5Lqs6YO95paw5a6_5Yy66KW_5paw5a6_77yX5LiB55uu77yY4oiS77yR77yTIOagl-WOn-ODk-ODq-ODh-OCo-ODs-OCsCAzZg!5e0!3m2!1sja!2sjp!4v1676017186797!5m2!1sja!2sjp"
              width="1500"
              height="450"
              allowFullScreen={true}
              loading="lazy"
            ></iframe>
          </Box>
          <Typography variant="body1" sx={{ padding: '15px 0', fontSize: { xs: '14px', sm: '16px' } }}>
            【店舗住所】東京都新宿区西新宿7-8-13 栗原ビル3F
          </Typography>

          <TopDetailButton width="250px" url="https://www.wifi-tokyo-rentalshop.com/service-campaign/store-receipt/">
            さらに詳しく見る
          </TopDetailButton>
        </Box>
        <TitleBar>安心補償オプション</TitleBar>
        <CompensationBlock />
        <Box
          sx={{
            textAlign: 'center',
            width: { xs: '100%', sm: '600px' },
            margin: '0 auto'
          }}
        >
          <Typography variant="body1" component="div" sx={{ padding: '10px 0', fontSize: { xs: '12px', sm: '14px' } }}>
            ※安心補償オプション：1日44円（税込）
          </Typography>
          <TopDetailButton width="250px" url="https://www.wifi-tokyo-rentalshop.com/service-campaign/anshin-hosyou/">
            さらに詳しく見る
          </TopDetailButton>
          <Box textAlign={'left'} sx={{ marginTop: '20px' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: '700',
                color: baseColors.red,
                borderBottom: `1px solid ${baseColors.red}`,
                margin: '10px 0'
              }}
            >
              故障・紛失について
            </Typography>
            <Typography variant="subtitle2">故障</Typography>
            <Typography variant="caption" gutterBottom sx={{ display: 'block' }}>
              お客様の過失によりレンタル商品の故障や破損等が発生した場合、修理代金もしくは本体代金をご請求いたします。
            </Typography>
            <Typography variant="subtitle2">紛失</Typography>
            <Typography variant="caption" gutterBottom sx={{ display: 'block' }}>
              レンタル商品の紛失・盗難等により、返却が出来なくなった場合、本体代金をご請求いたします。
            </Typography>
            <Typography variant="subtitle2">安心補償サービス</Typography>
            <Typography variant="caption" gutterBottom sx={{ display: 'block' }}>
              安心補償にご加入いただいた場合は、故障時の代金を全額免除させていただきます。
              ※盗難・紛失された場合は、補償費16,500円をご請求いたします。
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: '700',
                color: baseColors.red,
                borderBottom: `1px solid ${baseColors.red}`,
                margin: '10px 0'
              }}
            >
              お支払い方法
            </Typography>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 4, sm: 2 }}>
                <img src={visaImage} alt="VISA" style={{ width: '90px' }} />
              </Grid2>
              <Grid2 size={{ xs: 4, sm: 2 }}>
                <img src={mastercardImage} alt="MASTER CARD" style={{ width: '90px' }} />
              </Grid2>
              <Grid2 size={{ xs: 4, sm: 2 }}>
                <img src={jcbImage} alt="JCB" style={{ width: '90px' }} />
              </Grid2>
              <Grid2 size={{ xs: 4, sm: 2 }}>
                <img src={amexImage} alt="AMEX" style={{ width: '90px' }} />
              </Grid2>
              <Grid2 size={{ xs: 4, sm: 2 }}>
                <img src={dinersImage} alt="Diners" style={{ width: '90px' }} />
              </Grid2>
              <Grid2 size={{ xs: 4, sm: 2 }}>
                <img src={amazonImage} alt="AmazonPay" style={{ width: '90px' }} />
              </Grid2>
            </Grid2>
            <Typography variant="body1" sx={{ fontSize: '14px', padding: '10px 0 20px' }}>
              ※銀行振込みも対応してます。
              <br />
              ※店頭受取は上記支払い方法が使えます。
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: '700',
                color: baseColors.red,
                borderBottom: `1px solid ${baseColors.red}`,
                margin: '10px 0'
              }}
            >
              配送・受取りについて
            </Typography>
            <Typography variant="subtitle2">配送方法</Typography>
            <Typography variant="caption" gutterBottom sx={{ display: 'block' }}>
              ヤマト運輸・佐川急便・ゆうパックのいずれかの方法で配送を行います。※運送会社の指定は行っておりませんのでご了承ください
              返却は同封のゆうパケットをご使用ください。
            </Typography>
            <Typography variant="subtitle2">受取場所・方法</Typography>
            <Typography variant="caption">ホテル・空港・病院、その他ご指定のご住所。Wi-Fi東京の店頭にて受け取ることもできます。</Typography>
            <Typography variant="subtitle2">最短レンタル開始日</Typography>
            <Typography variant="caption" gutterBottom sx={{ display: 'block' }}>
              営業日の16時までのご注文で、最短翌日からご利用いただけます。
            </Typography>
          </Box>
        </Box>
        <TitleBar>よくある質問</TitleBar>
        <Box
          sx={{
            padding: { xs: '10px 0', sm: '10px' },
            width: { xs: '100%', sm: '800px' },
            margin: '0 auto'
          }}
        >
          <FaqAccordion
            question="レンタルのお申し込み方法を教えてください。"
            answer={
              <>
                <div>
                  WEBサイトご注文フォームからクレジットカード払いでのお申し込みの際に本人確認書類は原則必要ありません。
                  <br />
                  ただし、店頭受取で現金払いや銀行振込による支払いの場合は、ご本人様の身分確認ができる運転免許証などの顔写真付きの身分証明証のご提示とコピーをいただきます。
                  <br />
                  受取方法が郵送の場合は、メールあるいはLINEで身分証の写真を添付いただき確認させていただききます。
                </div>
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <TopNextButton onClick={() => handleNext('/rental/ec/')} />
                </div>
              </>
            }
          />
          <FaqAccordion
            question="レンタルに必要な書類はありますか？"
            answer={
              <span>
                WEBサイトご注文フォームからクレジットカード払いでのお申し込みの際に本人確認書類は原則必要ありません。
                <br />
                ただし、店頭受取で現金払いや銀行振込による支払いの場合は、ご本人様の身分確認ができる運転免許証などの顔写真付きの身分証明証のご提示とコピーをいただきます。
                <br />
                受取方法が郵送の場合は、メールあるいはLINEで身分証の写真を添付いただき確認させていただききます。
              </span>
            }
          />
          <FaqAccordion
            question="海外在住なので日本国内に住所かありません。利用できますか？"
            answer={
              <span>
                日本国内に住所をお持ちでない方も、WiFiレンタルは可能です。
                <br />
                ただし、お届けは日本国内のみとなっておりますので、宿泊先のホテル、日本国内で滞在する場所、受け取りを希望する場所の住所をご指定ください。
              </span>
            }
          />
          <FaqAccordion
            question="Wi-Fiルーターを空港で受け取ることはできますか？"
            answer={
              <span>
                はい、「空港郵便局」でお受け取りが可能です。
                <br />
                <br />
                お申し込みの際に、受け取り希望の空港郵便局住所をお届け先住所へご入力ください。
                <br />
                お名前とお電話番号は、実際に受け取られる方のご情報をご入力ください。
                <br />
                お受け取りいただける空港は、空港内郵便局へのお届けについての空港一覧よりご確認いただけます。
                <br />
                <br />
                空港郵便局でお受け取りの際は、身分証明書の提示を求められますので、運転免許証又はパスポートを忘れずにお持ちください。
                <br />
                <br />
                <a
                  style={{ textDecoration: 'underline', color: '#3fa4fc' }}
                  href="https://www.wifi-tokyo-rentalshop.com/service-campaign/airport/"
                >
                  ⇒空港内郵便局へのお届けについて
                </a>
              </span>
            }
          />
          <FaqAccordion
            question="通信容量の制限などはありますか？"
            answer={
              <div>
                <Typography variant="subtitle1">【サクッとプランサクッとプラン（1日3GB）／たっぷりプラン（1日5GB）】</Typography>
                <Typography variant="subtitle2" sx={{ paddingTop: '10px' }}>
                  ・サクッとプラン
                </Typography>
                1日3GBまで通常速度で通信いただけます。
                <br />
                1日3GB以上ご利用の場合、通信速度が128Kbpsに制限されます。
                <br />
                速度制限になった場合は、翌日0時に解除され、1日あたり3GBまで通常速度で通信いただけます。
                <br />
                上記以外の解除方法はございませんので、データ通信量の買い足しなどはいただけません。
                <br />
                <Typography variant="subtitle2" sx={{ paddingTop: '10px' }}>
                  ・たっぷりプラン
                </Typography>
                1日5GBまで通常速度で通信いただけます。
                <br />
                1日5GB以上ご利用の場合、通信速度が128Kbpsに制限されます。
                <br />
                速度制限になった場合は、翌日0時に解除され、1日あたり5GBまで通常速度で通信いただけます。
                <br />
                上記以外の解除方法はございませんので、データ通信量の買い足しなどはいただけません。
                <br />
                <br />
                ※ベストエフォート方式のため回線の混雑状況や通信環境などにより、通信速度が低下、または通信できなくなる場合があります。
                <br />
                対応エリアであっても稀に周辺環境の状況や回線混雑の時間帯により繋がりにくい、または繋がらない場合もございます。
                <br />
                実際にお使いいただかないと分かりかねる部分がございますこと、ご了承をお願いいたします。
                <Typography variant="subtitle1" sx={{ paddingTop: '10px' }}>
                  【無制限プラン】
                </Typography>
                データ容量無制限で通信いただけます。
                <br />
                <br />
                ※対応エリアであっても稀に周辺環境の状況や回線混雑の時間帯により繋がりにくい、または繋がらない場合もございます。
                <br />
                実際にお使いいただかないと分かりかねる部分がございますこと、ご了承をお願いいたします。
                <br />
              </div>
            }
          />
          <FaqAccordion
            question="Wi-Fiルーターを紛失または壊してしまいました。"
            answer={
              <div>
                <Typography
                  variant="body1"
                  sx={{
                    paddingBottom: '10px',
                    fontSize: { xs: '14px', sm: '16px' }
                  }}
                >
                  安心補償のご加入、未加入により損害金が異なります。 <br />
                  安心補償のご料金や補償内容は、安心補償についてよりご覧いただけます。
                  <br />
                  安心補償オプションは、新規申込時のみ加入が可能です。 <br />
                  延長注文時に継続されない場合、再加入はできませんのでご注意ください。
                </Typography>
                <Typography variant="subtitle1">【安心補償加入の場合】 </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    padding: '10px 0',
                    fontSize: { xs: '14px', sm: '16px' }
                  }}
                >
                  破損・水没：全額補償0円
                  <br />
                  紛失・盗難など：16,500円(税込)
                  <br />
                  レンタル開始日(宅配便受取：お届け指定日、店頭受取：店頭受取日)からレンタル終了日までが補償期間となります。
                  <br />
                  レンタル終了日以降にご連絡の場合、安心補償オプションをご利用いただく事は出来ません。
                  <br />
                  トラブルが発生しましたら直ちに当社までご連絡をお願いいたします。
                  <br />
                  通常使用による故障につきましては、代替品をお送りさせていただきます。
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    padding: '10px 0',
                    fontSize: { xs: '14px', sm: '16px' }
                  }}
                >
                  ※故障・破損・水濡れ・水没が発生した場合、端末の返却が確認できた場合に限り損害金を免除します。
                  <br />
                  ※紛失・盗難が発生した場合、警察署が発行する紛失届出証明書または紛失届出証明書を当社へ提示することで損害金の一部を免除します。
                  <br />
                  ※損害金をお支払いいただいた後に端末が発見された場合でも損害金の返金は行っておりません。
                </Typography>
                <Typography variant="subtitle1">【安心補償未加入の場合】</Typography>
                <Typography variant="body1" sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                  破損・水没・紛失・盗難など：44,000円(税込) 安心補償について
                </Typography>
              </div>
            }
          />
          <FaqAccordion
            question="付属品を紛失または壊してしまいました。"
            answer={
              <div>
                <Typography variant="body1" sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                  安心補償のご加入、未加入により損害金が異なります。 <br />
                  安心補償のご料金や補償内容は、安心補償についてよりご覧いただけます。
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    paddingTop: '10px',
                    fontSize: { xs: '14px', sm: '16px' }
                  }}
                >
                  安心補償オプションは、新規申込時のみ加入が可能です。
                  <br />
                  延長注文時に継続されない場合、再加入はできませんのでご注意ください。
                </Typography>
                <Typography variant="subtitle1" sx={{ paddingTop: '10px' }}>
                  【安心補償加入の場合】
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    paddingTop: '10px',
                    fontSize: { xs: '14px', sm: '16px' }
                  }}
                >
                  {' '}
                  ACアダプター・USBケーブル(充電ケーブル)・ポーチ：全額補償0円{' '}
                </Typography>
                <Typography variant="subtitle1" sx={{ paddingTop: '10px' }}>
                  {' '}
                  【安心補償未加入の場合】
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    paddingTop: '10px',
                    fontSize: { xs: '14px', sm: '16px' }
                  }}
                >
                  ACアダプター：1,800円(税込)
                  <br />
                  USBケーブル(充電ケーブル)：550円(税込)
                  <br />
                  ポーチ：550円(税込)
                  <br />
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    paddingTop: '10px',
                    fontSize: { xs: '14px', sm: '16px' }
                  }}
                >
                  当社でレンタル商品のご返却確認時にレンタル商品の不足、端末の故障等があった場合には、当社スタッフよりご連絡させていただきます。
                </Typography>
                安心補償について
              </div>
            }
          />
        </Box>
        <Box
          sx={{
            padding: { xs: '10px 0', sm: '10px' },
            width: '100%',
            margin: '0 auto',
            textAlign: 'center'
          }}
        >
          <TopDetailButton width="250px" url="https://www.wifi-tokyo-rentalshop.com/qanda/">
            さらに詳しく見る
          </TopDetailButton>
        </Box>
        <TitleBar>お知らせ</TitleBar>
        <BannerSlider />
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: '700',
            color: baseColors.red,
            borderBottom: `1px solid ${baseColors.red}`
          }}
        >
          Menu
        </Typography>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: { xs: '0px', sm: '20px' }
          }}
        >
          <TopFooterMenu />
        </Stack>
        <Box
          sx={{
            textAlign: 'center',
            padding: '20px',
            backgroundColor: baseColors.lightPink
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'center', alignItems: 'center' }}>
            <div>
              <img src={wifiTokyoImage} alt="Wifi東京" />
              <ThemeProvider theme={theme}>
                <List sx={{ fontSize: '14px' }}>
                  <ListItem sx={{ padding: '3px 15px' }}>
                    <ListItemIcon sx={{ minWidth: '20px' }}>
                      <BillIcon style={{ color: baseColors.black, width: '0.85em' }} />
                    </ListItemIcon>
                    <ListItemText>WiFi東京レンタルショップ</ListItemText>
                  </ListItem>
                  <ListItem sx={{ padding: '3px 15px' }}>
                    <ListItemIcon sx={{ minWidth: '20px' }}>
                      <InternetIcon style={{ color: baseColors.black, width: '0.85em' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontSize: '14px' }}>
                          <Link to="https://nitticompany.com">https://nitticompany.com</Link>
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem sx={{ padding: '3px 15px' }}>
                    <ListItemIcon sx={{ minWidth: '20px' }}>
                      <MapIcon style={{ color: baseColors.black, width: '0.85em' }} />
                    </ListItemIcon>
                    <ListItemText>
                      <Link to="https://goo.gl/maps/QoT9rMEZqYwEHPF96">
                        〒160-0023
                        <br />
                        東京都新宿区西新宿7-8-13
                        <br />
                        栗原ビル3F
                      </Link>
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ padding: '3px 15px' }}>
                    <ListItemIcon sx={{ minWidth: '20px' }}>
                      <TelIcon style={{ color: baseColors.black, width: '0.85em' }} />
                    </ListItemIcon>
                    <ListItemText>0120-295-755</ListItemText>
                  </ListItem>
                  <ListItem sx={{ padding: '3px 15px' }}>
                    <ListItemIcon sx={{ minWidth: '20px' }}>
                      <MailIcon style={{ color: baseColors.black, width: '0.85em' }} />
                    </ListItemIcon>
                    <ListItemText>info@wifi-tokyo-rentalshop.com</ListItemText>
                  </ListItem>
                  <ListItem sx={{ padding: '3px 15px' }}>
                    <ListItemIcon sx={{ minWidth: '20px' }}>
                      <ClockIcon style={{ color: baseColors.black, width: '0.85em' }} />
                    </ListItemIcon>
                    <ListItemText>
                      【営業時間】10~19時（土日祝日休み）
                      <br />
                      【店頭受取】10時30分～18時（土日祝日休み）
                      <br />
                      【電話応対時間】11～17時（土日祝日休み）
                    </ListItemText>
                  </ListItem>
                </List>
              </ThemeProvider>
            </div>
            <div style={{ backgroundColor: baseColors.white, padding: '10px' }}>
              <Typography variant="subtitle1" sx={{ color: baseColors.red }}>
                English information
              </Typography>
              <ThemeProvider theme={theme}>
                <List>
                  <ListItem sx={{ padding: '3px 15px' }}>
                    <ListItemIcon sx={{ minWidth: '20px' }}>
                      <BillIcon style={{ color: baseColors.black, width: '0.85em' }} />
                    </ListItemIcon>
                    <ListItemText>Wi-Fi Tokyo Rental Shop.</ListItemText>
                  </ListItem>
                  <ListItem sx={{ padding: '3px 15px' }}>
                    <ListItemIcon sx={{ minWidth: '20px' }}>
                      <InternetIcon style={{ color: baseColors.black, width: '0.85em' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontSize: '14px' }}>
                          <Link to="https://nitticompany.com">https://nitticompany.com</Link>
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem sx={{ padding: '3px 15px' }}>
                    <ListItemIcon sx={{ minWidth: '20px' }}>
                      <MapIcon style={{ color: baseColors.black, width: '0.85em' }} />
                    </ListItemIcon>
                    <ListItemText>
                      <Link to="https://goo.gl/maps/QoT9rMEZqYwEHPF96">
                        〒160-0023
                        <br />
                        7-8-13, Nishishinjuku, Shinjuku-ku, Tokyo.
                        <br />
                        Kurihara Building 3F.
                      </Link>
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ padding: '3px 15px' }}>
                    <ListItemIcon sx={{ minWidth: '20px' }}>
                      <TelIcon style={{ color: baseColors.black, width: '0.85em' }} />
                    </ListItemIcon>
                    <ListItemText>+81-3-6756-0255</ListItemText>
                  </ListItem>
                  <ListItem sx={{ padding: '3px 15px' }}>
                    <ListItemIcon sx={{ minWidth: '20px' }}>
                      <MailIcon style={{ color: baseColors.black, width: '0.85em' }} />
                    </ListItemIcon>
                    <ListItemText>info@wifi-tokyo-rentalshop.com</ListItemText>
                  </ListItem>
                  <ListItem sx={{ padding: '3px 15px' }}>
                    <ListItemIcon sx={{ minWidth: '20px' }}>
                      <ClockIcon style={{ color: baseColors.black, width: '0.85em' }} />
                    </ListItemIcon>
                    <ListItemText>
                      [Business hours] Mon.-Fri.10 am to 7pm.
                      <br />
                      Sat,Sun. and holidays are closed.
                      <br />
                      [Shop's counter] Mon.-Fri.10:30 am to 6pm.
                      <br />
                      Sat,Sun. and holidays are closed.
                      <br />
                      ※Last order time　4pm
                      <br />
                      ※Last pick up　6pm
                    </ListItemText>
                  </ListItem>
                </List>
              </ThemeProvider>
            </div>
          </Stack>
        </Box>
        <Box sx={{ padding: '10px', backgroundColor: baseColors.gray }}>
          <List
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: isMobile ? 'space-around' : 'center'
            }}
          >
            <ListItem disableGutters sx={{ width: isMobile ? '25%' : 'auto', padding: 0 }}>
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '12px',
                      borderRight: `1px solid ${baseColors.lineGray}`,
                      textAlign: 'center',
                      padding: { xs: '0px', sm: '0 10px' }
                    }}
                  >
                    <a href="https://www.wifi-tokyo-rentalshop.com/company-profile/">会社概要</a>
                  </Typography>
                }
              >
                会社概要
              </ListItemText>
            </ListItem>
            <ListItem disableGutters sx={{ width: isMobile ? '50%' : 'auto', padding: '0' }}>
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '12px',
                      borderRight: `1px solid ${baseColors.lineGray}`,
                      textAlign: 'center',
                      padding: { xs: '0px', sm: '0 10px' }
                    }}
                  >
                    <a href="https://www.wifi-tokyo-rentalshop.com/company-profile/">個人情報保護方針</a>
                  </Typography>
                }
              ></ListItemText>
            </ListItem>
            <ListItem disableGutters sx={{ width: isMobile ? '25%' : 'auto', padding: '0' }}>
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '12px',
                      borderRight: `1px solid ${baseColors.lineGray}`,
                      textAlign: 'center',
                      padding: { xs: '0px', sm: '0 10px' }
                    }}
                  >
                    <a href="https://www.wifi-tokyo-rentalshop.com/terms-of-service/">利用規約</a>
                  </Typography>
                }
              ></ListItemText>
            </ListItem>
            <ListItem disableGutters sx={{ width: isMobile ? '100%' : 'auto', padding: '0' }}>
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '12px',
                      borderRight: `1px solid ${baseColors.lineGray}`,
                      textAlign: 'center',
                      padding: { xs: '0px', sm: '0 10px' }
                    }}
                  >
                    <a href="https://www.wifi-tokyo-rentalshop.com/sct-law/">特定商取引に関する法律に基づく表記</a>
                  </Typography>
                }
              ></ListItemText>
            </ListItem>
            <ListItem disableGutters sx={{ width: isMobile ? '50%' : 'auto', padding: '0' }}>
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '12px',
                      borderRight: `1px solid ${baseColors.lineGray}`,
                      textAlign: 'center',
                      padding: { xs: '0px', sm: '0 10px' }
                    }}
                  >
                    <a href="https://www.wifi-tokyo-rentalshop.com/contact_us/">お問い合わせ</a>
                  </Typography>
                }
              ></ListItemText>
            </ListItem>
            <ListItem disableGutters sx={{ width: isMobile ? '50%' : 'auto', padding: '0' }}>
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '12px',
                      borderRight: `1px solid ${baseColors.lineGray}`,
                      textAlign: 'center',
                      padding: { xs: '0px', sm: '0 10px' }
                    }}
                  >
                    <a href="https://www.wifi-tokyo-rentalshop.com/service-campaign/corporate-contract/">法人のお客様へ</a>
                  </Typography>
                }
              ></ListItemText>
            </ListItem>
          </List>
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: baseColors.white,
            backgroundColor: baseColors.red,
            textAlign: 'center',
            padding: '10px'
          }}
        >
          © 2015 - 2025, WiFi Tokyo Renatal Shop.
        </Typography>
      </Container>
      <Stack
        direction="column"
        sx={{
          display: { xs: 'none', sm: 'flex' },
          position: 'fixed',
          top: '200px',
          right: 0
        }}
      >
        <VerticalButton width="auto" children="レンタルはこちら" icon={<SmartphoneWifiIcon />} color={baseColors.orange} url="/rental/ec" />
        <VerticalButton width="auto" children="延長する" icon={<UilCalenderIcon />} color={baseColors.red} url="/rental/mypage/" />
      </Stack>
    </>
  );
};
export default Top;
